<template>
  <v-off-canvas
    id="case-quote-off-canvas"
    :backdrop-static="true"
    offcanvas-class="offcanvas-end"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
        <h5 class="offcanvas-title">Send Quote</h5>
      </off-canvas-header>

      <off-canvas-body>
        <div class="show-preview">
          <div
            :id="`collapse-header-case-pax-preview`"
            :aria-controls="`collapse-case-pax-preview`"
            :data-bs-target="`#collapse-case-pax-preview`"
            aria-expanded="true"
            class="d-flex justify-content-between align-items-center accordion-toggle collapsed"
            data-bs-toggle="collapse"
          >
            Show Preview
            <v-icon class="accordion-icon" icon="chevron-up" />
          </div>
          <div id="collapse-case-pax-preview" class="accordion-body collapse p-0">
            <div class="p-1 mt-8">
              <div class="mb-4 border rounded pax-card" v-for="(pax, index) in invoicePayload" :key="index">
                <div
                  :id="`collapse-body-case-pax-${index}-preview`"
                  :aria-controls="`collapse-case-pax-${index}-preview`"
                  :data-bs-target="`#collapse-case-pax-${index}-preview`"
                  aria-expanded="true"
                  class="bg-light border-bottom d-flex justify-content-between pax-header accordion-toggle collapsed"
                  data-bs-toggle="collapse"
                >
                  <div class="d-flex align-items-center gap-3">
                    <div>
                      <div class="fw-bold fs-6 pax-name">{{ pax.name }}</div>
                      <div class="text-muted small pax-email">{{ pax.email }}</div>
                    </div>
                  </div>
                  <div class="d-flex flex-column justify-content-center pax-prices-days">
                    <div class="fw-semibold pax-price">€ {{ pax.waw_price || pax.quote }}</div>
                    <div class="small text-muted pax-days" v-if="pax.summary">{{ pax.summary.days }} Days</div>
                  </div>
                  <v-icon class="accordion-icon" icon="chevron-up" size="lg" style="color: white" />
                </div>
                <div class="p-3 pax-services accordion-body collapse" :id="`collapse-case-pax-${index}-preview`">
                  <div v-for="(service, sIndex) in pax.services_overview" :key="sIndex" class="mb-3">
                    <div class="fw-semibold text-primary mb-2 route-label">
                      {{ service.route }}
                    </div>

                    <div
                      v-for="(item, iIndex) in service.items"
                      :key="iIndex"
                      class="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-light-subtle service-item"
                    >
                      <div class="d-flex align-items-center gap-2">
                        <div>
                          <div class="fw-medium item-name">
                            {{ item.name }}
                          </div>
                          <div class="text-muted small item-resource">
                            {{ item.resource_name }}
                          </div>
                        </div>
                      </div>
                      <div class="text-end">
                        <span class="badge bg-secondary qty-badge"> x{{ item.qty }} </span>
                        <div class="small text-muted type-badge">
                          {{ item.item_type }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <form id="case-send-quote-form" class="d-flex flex-column justify-content-between h-100">
          <v-form-input
            id="client-email"
            v-model="freeEmail"
            :label="$t('general.shared.email')"
            autocomplete="off"
            :yup-errors-variable="errors.freeEmail"
            @blur="validateFreeEmail"
            form-type="outline"
            type="email"
          />
          <v-checkbox
            id="show-hotels"
            class="mt-16"
            :label="$t('general.invoice.showHotels')"
            :model-value="showHotels"
            @update:model-value="showHotels = $event"
          />
          <div class="mt-32">
            <template v-for="pax in invoicePayload" :key="pax.id">
              <v-checkbox
                class="mb-16"
                :label="`${pax.name} (${pax.email})`"
                :model-value="paxIds.includes(pax.id)"
                :isDisabled="!pax.email"
                @update:model-value="togglePax(pax.id, $event)"
              />
            </template>
          </div>
          <div class="show-preview">
            <div
              :id="`collapse-header-case-extra-info`"
              :aria-controls="`collapse-case-extra-info`"
              :data-bs-target="`#collapse-case-extra-info`"
              aria-expanded="true"
              class="d-flex justify-content-between align-items-center accordion-toggle collapsed"
              data-bs-toggle="collapse"
            >
              Extra Info
              <v-icon class="accordion-icon" icon="chevron-up" />
            </div>
            <div id="collapse-case-extra-info" class="accordion-body collapse p-0">
              <form id="case-tour-update-include-form" class="d-flex flex-column justify-content-between h-100">
                <fieldset v-for="language in languages" :key="language.shortName">
                  <div class="form-outline" v-if="currentLanguage === language.shortName">
                    <label class="label">
                      <span>{{ $t("clientTour.include") }}</span>
                    </label>
                    <v-html-editor
                      :id="'case-tour-update-include-' + language.shortName"
                      :ref="'editor_' + language.shortName"
                      :value="include[language.shortName].description"
                      @input="include[language.shortName].description = $event"
                      @media-uploaded="editorMedia.push($event)"
                      :height="'250px'"
                    />
                  </div>
                </fieldset>
              </form>
              <form id="case-tour-update-not-include-form" class="d-flex flex-column justify-content-between h-100 mt-16">
                <fieldset v-for="language in languages" :key="language.shortName">
                  <div class="form-outline" v-if="currentLanguage === language.shortName">
                    <label class="label">
                      <span>{{ $t("clientTour.notInclude") }}</span>
                    </label>
                    <v-html-editor
                      :id="'case-tour-update-not-include-' + language.shortName"
                      :ref="'editor_not_include_' + language.shortName"
                      :value="notInclude[language.shortName].description"
                      @input="(notInclude[language.shortName].description = $event)"
                      @media-uploaded="editorMedia.push($event)"
                      :height="'250px'"
                    />
                  </div>
                </fieldset>
              </form>
              <form id="case-tour-update-optional-form" class="d-flex flex-column justify-content-between h-100 mt-16">
                <fieldset v-for="language in languages" :key="language.shortName">
                  <div class="form-outline" v-if="currentLanguage === language.shortName">
                    <label class="label">
                      <span>{{ $t("clientTour.optional") }}</span>
                    </label>
                    <v-html-editor
                      :id="'case-tour-update-optional-' + language.shortName"
                      :ref="'editor_optional_' + language.shortName"
                      :value="optional[language.shortName].description"
                      @input="(optional[language.shortName].description = $event)"
                      @media-uploaded="editorMedia.push($event)"
                      :height="'250px'"
                    />
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
          <v-button
            :is-loading="accepted"
            :disabled="!validateFreeEmail || accepted"
            class="btn-submit-form btn-icon w-fit align-self-end mt-16"
            size="sm"
            variant="primary"
            @click="sendQuote"
          >
             {{ $t("general.button.save") }}
            <v-icon icon="arrow-right" size="sm" space="ms-12" />
          </v-button>
        </form>
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script setup name="TheCaseQuoteOffCanvas">
import VOffCanvas from "@/components/vendor/basic/offcanvas/VOffCanvas.vue";
import OffCanvasHeader from "@/components/vendor/basic/offcanvas/OffCanvasHeader.vue";
import OffCanvasBody from "@/components/vendor/basic/offcanvas/OffCanvasBody.vue";
import ResizableContainer from "@/components/resizablecontainer/ResizableContainer.vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { VFormInput, VCheckbox } from "@uniqoders/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import * as yup from "yup";
import { computed, nextTick, ref, watch } from "vue";
import api from "@/api";
import useConcepts from "@/helpers/Concepts";
import { useAppLanguages, useLocaleKeyedRef } from "@/composables/useAppLanguages";
import { getCurrentInstance } from "vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";

const { t } = useI18n();
const toast = useToast();
const { concepts } = useConcepts();
const { languages, locales, resolveStoredLocale, defaultLocale } = useAppLanguages();

const include = useLocaleKeyedRef(() => ({ description: '' }));
const notInclude = useLocaleKeyedRef(() => ({ description: '' }));
const optional = useLocaleKeyedRef(() => ({ description: '' }));

const schema = yup.object({
  freeEmail: yup.string().nullable().email("Invalid email format").notRequired(),
});

const freeEmail = ref("");
const showHotels = ref(false);
const paxIds = ref([]);
const accepted = ref(false);
const errors = ref({ freeEmail: "" });
const currentLanguage = computed(() => resolveStoredLocale() || defaultLocale.value);
const instance = getCurrentInstance();
const proxy = instance?.proxy;

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  caseId: {
    type: Number,
    required: true,
  },
  invoicePayload: {
    type: Array,
    required: true,
  },
  clientTour: {
    type: Object,
    default: () => ({}),
  },
  tripInfo: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(["closed", "loadQuote", "getCaseData"]);

const prefillData = () => {
  if (props.tripInfo) {
    Object.keys(include.value).forEach((locale) => {
      include.value[locale].description = props.tripInfo.included || "";
      notInclude.value[locale].description = props.tripInfo.not_included || "";
      optional.value[locale].description = props.tripInfo.optional || "";
    });
    return;
  }

  const includeData = props.clientTour?.included_trip_information?.translations || [];
  const notIncludeData = props.clientTour?.excluded_trip_information?.translations || [];
  const optionalData = props.clientTour?.optional_trip_information?.translations || [];

  includeData.forEach((translation) => {
    if (include.value[translation.locale]) {
      include.value[translation.locale].description = translation.description;
    }
  });

  notIncludeData.forEach((translation) => {
    if (notInclude.value[translation.locale]) {
      notInclude.value[translation.locale].description = translation.description;
    }
  });

  optionalData.forEach((translation) => {
    if (optional.value[translation.locale]) {
      optional.value[translation.locale].description = translation.description;
    }
  });
};

watch(locales, (list) => {
  if (list.length) {
    prefillData();
  }
}, { immediate: true });

const validateFreeEmail = async () => {
  try {
    await schema.validateAt("freeEmail", {
      freeEmail: freeEmail.value,
    });

    errors.value.freeEmail = "";
  } catch (err) {
    errors.value.freeEmail = err.message;
  }
};

const togglePax = (id, checked) => {
  if (checked) {
    if (!paxIds.value.includes(id)) {
      paxIds.value.push(id);
    }
  } else {
    paxIds.value = paxIds.value.filter((p) => p !== id);
  }
};

const addIncludeInEditor = (text) => {
  nextTick(() => {
    const refName = "editor_" + currentLanguage.value;
    let editorComponent = proxy?.$refs?.[refName];

    if (Array.isArray(editorComponent)) {
      editorComponent = editorComponent[0];
    }
    if (!editorComponent) {
      console.warn("Editor not found:", refName);
      return;
    }
    editorComponent.insertText(text);
  });
};

const addNotIncludeInEditor = (text) => {
  nextTick(() => {
    const refName = "editor_not_include_" + currentLanguage.value;
    let editorComponent = proxy?.$refs?.[refName];

    if (Array.isArray(editorComponent)) {
      editorComponent = editorComponent[0];
    }
    if (!editorComponent) {
      console.warn("Editor not found:", refName);
      return;
    }
    editorComponent.insertText(text);
  });
};

const addOptionalInEditor = (text) => {
  nextTick(() => {
    const refName = "editor_optional_" + currentLanguage.value;
    let editorComponent = proxy?.$refs?.[refName];

    if (Array.isArray(editorComponent)) {
      editorComponent = editorComponent[0];
    }
    if (!editorComponent) {
      console.warn("Editor not found:", refName);
      return;
    }
    editorComponent.insertText(text);
  });
};

paxIds.value = (props.invoicePayload || []).map((pax) => pax.id);

const sendQuote = async () => {
  accepted.value = true;
  try {
    const selectedPax = (props.invoicePayload || []).filter((pax) => paxIds.value.includes(pax.id));
    const data = selectedPax.map((pax) => ({
      pax_id: pax.id,
      batch_id: pax.batch_id ?? null,
      profit: pax.profit,
      margin: pax.margin,
      waw_price: pax.quote,
      waw_price_locked: pax.locked,
    }));

    if (!data.length) {
      toast.error("No paxes selected for the quote");
      return;
    }

    const payload = {
      quotes: data,
      free_email: freeEmail.value,
      show_hotels: showHotels.value,
      pax_ids: paxIds.value,
      include: include.value,
      not_include: notInclude.value,
      optional: optional.value
    };

    const response = await api.case.sendQuote(props.agency, Number(props.caseId), payload);
    if (response) {
      toast.success(t("general.shared.savedChanges"));
      emit("getCaseData");
      emit("loadQuote");
      emit("closed");
    }
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data?.message);
  } finally {
    accepted.value = false;
  }
};
</script>

<style scoped>
.accordion-toggle {
  cursor: pointer;
}

.show-preview {
  border-radius: 0.5rem;
  box-shadow: 0 1px 0.5rem var(--uq-gray-300);
  background-color: var(--uq-white);
  padding: 1rem;
  margin-bottom: 1rem;
}

.pax-card {
  background: #ffffff;
  border: 1.5px solid #e4e8f5 !important;
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(60, 80, 160, 0.07) !important;
  transition: box-shadow 0.2s;
}
.pax-card:hover {
  box-shadow: 0 8px 32px rgba(60, 80, 160, 0.13) !important;
}

.pax-header {
  background: linear-gradient(110deg, #ee2a7b 0%, #ee2a7b 60%, #ee2a7b 100%) !important;
  padding: 15px 24px !important;
  border-bottom: none !important;
}
.pax-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -1px;
}
.pax-name {
  font-weight: 700;
  font-size: 1rem;
  color: #fff !important;
}
.pax-email {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65) !important;
}
.pax-price {
  display: flex;
  font-weight: 700;
  font-size: 1.2rem;
  color: #fff !important;
}
.pax-prices-days {
  margin-right: 1.5rem;
}
.pax-days {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6) !important;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 2px 10px;
  display: inline-block;
  margin-top: 4px;
}

.pax-services {
  padding: 20px 24px !important;
  background: #fafbff;
}

.route-label {
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #ee2a7b !important;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px !important;
}
.route-label::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, #c5caf5, transparent);
}

.service-item {
  background: #ffffff !important;
  border: 1.5px solid #eaecf8 !important;
  border-radius: 10px !important;
  padding: 12px 16px !important;
  margin-bottom: 8px !important;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.service-item:hover {
  border-color: #9fa8da !important;
  box-shadow: 0 2px 10px rgba(57, 73, 171, 0.08);
}

.item-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #ee2a7b;
}
.item-resource {
  font-size: 0.75rem;
  color: #070607 !important;
}
.qty-badge {
  background: #ee2a7b !important;
  color: #fff !important;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  margin-right: 6px;
}
.type-badge {
  font-size: 0.68rem;
  color: #8892b0;
  background: #f0f1fa;
  border-radius: 20px;
  padding: 2px 8px;
  margin-top: 4px;
  display: inline-block;
}

.toggle-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ee2a7b;
  display: inline-block;
  margin-right: 8px;
}

.labled-button {
  color: #000;
  cursor: pointer;
  padding: 0.4rem 1rem;
  font-size: 0.75rem;
  height: auto;
  border-radius: 10px;
}

.variable-badge {
  font-size: 0.55rem !important;
  padding: 1px 6px !important;
  border-radius: 4px !important;
  height: 25px !important;
  min-height: 25px !important;
  line-height: 1 !important;
  background-color: #000 !important;
  color: #fff !important;
  border: none !important;
}
</style>
